/**
 * VIBRATION — boulder → flask buzz and the player's on/off switch (volume dialog), every build.
 *
 * Output, first match wins:
 * - App (Capacitor): the native shell registers a player (`setNativePlayer`) on the Haptics plugin.
 * - Touch browsers with `navigator.vibrate` (Android Chrome, Samsung Internet): short patterns.
 *   A call waits for this frame's sticky user activation and a visible page. Chrome blocks earlier
 *   calls and logs "Blocked call to navigator.vibrate …" — inside a portal iframe too — so we never
 *   make one.
 * - iOS browsers and desktop: nothing to drive; `isAvailable()` is false and the switch is hidden.
 *
 * Kinds: `hit` — boulder cost HP; `break` — that hit emptied HP (flask shatters);
 * `shield` — the shield took the hit. Every boulder–flask contact emits exactly one of
 * `fall:boulder` / `fall:shield_block` (run/fall-phase.js `_onBoulderHit`).
 */

export const VIBRATION_HIT = "hit";
export const VIBRATION_BREAK = "break";
export const VIBRATION_SHIELD = "shield";

/** `navigator.vibrate` patterns (ms) for browsers; the app uses native impact styles instead. */
export const WEB_VIBRATION_PATTERNS = {
  hit: 35,
  break: [70, 60, 140],
  shield: 20,
};

var enabled = true;
var nativePlayer = null;

function ignore() {}

/** Phones / tablets whose browser exposes the Vibration API. Desktop Chrome has it too, with no motor. */
function hasWebVibration() {
  if (typeof navigator === "undefined" || !navigator || typeof navigator.vibrate !== "function") {
    return false;
  }
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  try {
    return window.matchMedia("(pointer: coarse)").matches;
  } catch (e) {
    return false;
  }
}

/** Same gate Chrome applies before vibrating; without the API we cannot tell, so we stay silent. */
function webVibrationAllowedNow() {
  if (typeof document !== "undefined" && document.visibilityState === "hidden") return false;
  var activation = navigator.userActivation;
  return !!(activation && activation.hasBeenActive);
}

function playWeb(kind) {
  if (!hasWebVibration() || !webVibrationAllowedNow()) return;
  var pattern = WEB_VIBRATION_PATTERNS[kind];
  if (pattern == null) return;
  try {
    navigator.vibrate(pattern);
  } catch (e) {
    ignore(e);
  }
}

export const Vibration = {
  /** True when this device can buzz (app shell registered, or a touch browser with the API). */
  isAvailable: function () {
    return !!nativePlayer || hasWebVibration();
  },

  isEnabled: function () {
    return enabled;
  },

  /** @param {boolean} value — anything but `false` turns it on (missing saved value = on). */
  setEnabled: function (value) {
    enabled = value !== false;
  },

  /** @param {((kind: string) => (Promise<unknown> | void)) | null} play */
  setNativePlayer: function (play) {
    nativePlayer = typeof play === "function" ? play : null;
  },

  /** @param {string} kind */
  play: function (kind) {
    if (!enabled) return;
    if (!nativePlayer) {
      playWeb(kind);
      return;
    }
    try {
      var p = nativePlayer(kind);
      if (p && typeof p.catch === "function") p.catch(ignore);
    } catch (e) {
      // Vibration is cosmetic; never let a platform error reach gameplay.
    }
  },
};

/**
 * @param {{ on: Function, off: Function }} bus
 * @param {(kind: string) => void} [play] — defaults to `Vibration.play`
 * @returns {() => void} uninstall
 */
export function installBoulderVibration(bus, play) {
  var fire = typeof play === "function" ? play : Vibration.play;
  if (!bus || typeof bus.on !== "function") return function () {};
  function safeFire(kind) {
    try {
      fire(kind);
    } catch (e) {
      ignore(e);
    }
  }
  function onBoulder(data) {
    safeFire(data && data.isHpDepleted ? VIBRATION_BREAK : VIBRATION_HIT);
  }
  function onShieldBlock() {
    safeFire(VIBRATION_SHIELD);
  }
  bus.on("fall:boulder", onBoulder);
  bus.on("fall:shield_block", onShieldBlock);
  return function () {
    if (typeof bus.off !== "function") return;
    bus.off("fall:boulder", onBoulder);
    bus.off("fall:shield_block", onShieldBlock);
  };
}

/** @internal tests */
export function __resetVibration() {
  enabled = true;
  nativePlayer = null;
}
